import { FC, useState, createContext, useContext, useCallback, useMemo } from 'react'
import {
  QueryState,
  QueryRequestContextProps,
  initialQueryRequest,
  WithChildren,
} from '../../../../../_metronic/helpers'

const QueryRequestContext = createContext<QueryRequestContextProps>(initialQueryRequest)

const QueryRequestProvider: FC<WithChildren> = ({children}) => {
  const [state, setState] = useState<QueryState>(initialQueryRequest.state)

  // Usar useCallback para memoizar updateState y evitar re-renders innecesarios
  const updateState = useCallback((updates: Partial<QueryState>) => {
    setState(prevState => {
      // Crear el estado actualizado
      const updatedState = { ...prevState, ...updates } as QueryState
      
      // Comparar valores incluyendo undefined/null para detectar cambios reales
      const hasChanges = Object.keys(updates).some(key => {
        const prevValue = prevState[key as keyof QueryState]
        const newValue = updatedState[key as keyof QueryState]
        
        // Comparación profunda para objetos (como filter)
        if (key === 'filter') {
          const prevFilter = prevValue as Record<string, unknown> | undefined
          const newFilter = newValue as Record<string, unknown> | undefined
          
          // Si ambos son undefined/null, no hay cambio
          if (!prevFilter && !newFilter) return false
          // Si uno es undefined y el otro no, hay cambio
          if (!prevFilter || !newFilter) return true
          // Comparar las claves de los objetos
          const prevKeys = Object.keys(prevFilter).sort()
          const newKeys = Object.keys(newFilter).sort()
          if (prevKeys.length !== newKeys.length) return true
          return prevKeys.some(k => prevFilter[k] !== newFilter[k])
        }
        
        return prevValue !== newValue
      })
      
      return hasChanges ? updatedState : prevState
    })
  }, [])

  // Memoizar el valor del provider para evitar re-renders innecesarios
  const providerValue = useMemo(() => ({
    state,
    updateState
  }), [state, updateState])

  return (
    <QueryRequestContext.Provider value={providerValue}>
      {children}
    </QueryRequestContext.Provider>
  )
}

const useQueryRequest = () => useContext(QueryRequestContext)
export { QueryRequestProvider, useQueryRequest }
